import axios from 'axios';

import { API_URL, DEPLOY_ENV, DeployEnv } from '~/common/utils/env.util.ts';
import { deepTrim } from '~/common/utils/trim.util.ts';

import type { AxiosError, AxiosInstance } from 'axios';

import type { ApiErrorResponseData } from '~/common/types/api.type';
import type { NullableString } from '~/common/types/nullable.type.ts';

export const axiosInstance = axios.create({
  baseURL: API_URL,
});

export function setAxiosAuthorization(instance: AxiosInstance, accessToken?: NullableString) {
  if (!accessToken) {
    delete instance.defaults.headers.common.Authorization;
    return;
  }

  instance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
}

function setInterceptors(instance: AxiosInstance) {
  instance.interceptors.request.use(config => {
    // FormData는 deepTrim 처리하지 않음
    if (config.data && !(config.data instanceof FormData)) {
      config.data = deepTrim(config.data);
    }

    if (config.params) {
      config.params = deepTrim(config.params);
    }

    return config;
  });

  const isNotProduction = typeof window !== 'undefined' && DEPLOY_ENV !== DeployEnv.PRODUCTION;

  instance.interceptors.response.use(
    response => {
      // * 브라우저에서 개발 중에 어떠한 응답이 수신되고 있는지를 알려줍니다.
      if (isNotProduction) {
        window.console.debug(
          `[${response.config.method?.toUpperCase()}] ${response.config.url}`,
          response.config.params || '',
          response.config.data || '',
        );
      }
      return response;
    },

    error => {
      // * 브라우저에서 개발 중에 어떠한 응답이 수신되고 있는지를 알려줍니다.
      if (isNotProduction) {
        window.console.error(error);
      }

      // 401 에러는 useAuth hook에서 처리합니다

      return Promise.reject(error);
    },
  );
}

setInterceptors(axiosInstance);

export function isApiError<T = ApiErrorResponseData>(error: unknown): error is AxiosError<T> {
  return axios.isAxiosError(error);
}
